import { createTheme } from '@mui/material/styles';

const T = {
  blue: '#005EAD',
  blueHover: '#004C8C',
  blueDeep: '#003D73',
  tint: '#EAF2FB',
  ink: '#101828',
  text: '#1F2937',
  text2: '#475467',
  text3: '#667085',
  border: '#D0D5DD',
  field: '#667085',
  divider: '#E4E7EC',
  subtle: '#F9FAFB',
  strip: '#F2F4F7',
  footer: '#0B1B2B',
  footerText: '#C9D3DF',
  focus: '#F2B705',
  success: '#067647',
  successSoft: '#ECFDF3',
  successLine: '#ABEFC6',
  warn: '#B54708',
  warnSoft: '#FFFAEB',
  error: '#B42318',
  errorSoft: '#FEF3F2',
} as const;

const focusRing = { outline: `3px solid ${T.focus}`, outlineOffset: 2 };

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: T.blue, dark: T.blueHover, light: T.tint, contrastText: '#FFFFFF' },
    secondary: { main: T.blueDeep, contrastText: '#FFFFFF' },
    success: { main: T.success },
    warning: { main: T.warn },
    error: { main: T.error },
    info: { main: T.blue },
    text: { primary: T.text, secondary: T.text2, disabled: T.text3 },
    background: { default: '#FFFFFF', paper: '#FFFFFF' },
    divider: T.divider,
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif',
    body1: { lineHeight: 1.6 },
    h1: { fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.08, color: T.ink },
    h2: { fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2, color: T.ink },
    h3: { fontWeight: 700, letterSpacing: '-0.02em', color: T.ink },
    h4: { fontWeight: 700, letterSpacing: '-0.02em', color: T.ink },
    h5: { fontWeight: 700, color: T.ink },
    h6: { fontWeight: 700, color: T.ink },
    subtitle1: { fontWeight: 600, color: T.ink },
    subtitle2: { fontWeight: 600, color: T.ink },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '1rem' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { WebkitFontSmoothing: 'antialiased', color: T.text },
        'a:focus-visible, button:focus-visible': focusRing,
        '@keyframes ast-page-enter': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: true },
      styleOverrides: {
        root: {
          borderRadius: 4,
          minHeight: 44,
          paddingInline: 20,
          '&.Mui-focusVisible': focusRing,
        },
        sizeLarge: { minHeight: 52, paddingInline: 28, fontSize: '1.0625rem' },
        sizeSmall: { minHeight: 36, paddingInline: 12, fontSize: '0.875rem' },
        containedPrimary: { '&:hover': { backgroundColor: T.blueHover } },
        outlined: { borderWidth: 2, '&:hover': { borderWidth: 2 } },
        text: { paddingInline: 8, textDecoration: 'underline', textUnderlineOffset: 3 },
      },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { fontWeight: 600 } },
    },
    MuiPaper: {
      styleOverrides: { outlined: { borderColor: T.border } },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: { root: { borderRadius: 8, borderColor: T.border } },
    },
    MuiInputLabel: {
      styleOverrides: { root: { color: T.text2 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: '#FFFFFF',
          '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2, borderColor: T.field },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.ink },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.ink, borderWidth: 2 },
          '&.Mui-focused': { outline: `3px solid ${T.focus}`, outlineOffset: 0 },
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, borderRadius: 999 } },
    },
    MuiTab: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 500,
          minHeight: 56,
          paddingInline: 0,
          minWidth: 0,
          marginRight: 32,
          color: T.text2,
          '&.Mui-selected': { color: T.ink, fontWeight: 700 },
          '&.Mui-focusVisible': focusRing,
        },
      },
    },
    MuiTabs: {
      styleOverrides: { indicator: { height: 3 } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
  },
});

export const tokens = T;
